import { useEffect, useRef, useState } from 'react'
import { createChart, IChartApi, ISeriesApi, CandlestickData, HistogramData } from 'lightweight-charts'
import { X, Loader2 } from 'lucide-react'
import axios from 'axios'

const API_BASE = import.meta.env.PROD ? '/api' : 'http://localhost:9099/api'

interface InstrumentChartModalProps {
  instrumentId: string
  onClose: () => void
}

export default function InstrumentChartModal({ instrumentId, onClose }: InstrumentChartModalProps) {
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null)
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<{ total_bars: number; displayed_bars: number } | null>(null)

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  // Create chart and load data
  useEffect(() => {
    if (!chartContainerRef.current) return

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
      layout: {
        background: { color: '#0a0a0a' },
        textColor: '#a0a0a0',
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: '#1a1a1a' },
        horzLines: { color: '#1a1a1a' },
      },
      crosshair: {
        mode: 1,
        vertLine: {
          color: '#4488ff',
          width: 1,
          style: 2,
          labelBackgroundColor: '#4488ff',
        },
        horzLine: {
          color: '#4488ff',
          width: 1,
          style: 2,
          labelBackgroundColor: '#4488ff',
        },
      },
      rightPriceScale: {
        borderColor: '#2a2a2a',
      },
      leftPriceScale: {
        visible: true,
        borderColor: '#2a2a2a',
      },
      timeScale: {
        borderColor: '#2a2a2a',
        timeVisible: true,
        secondsVisible: false,
      },
    })

    chartRef.current = chart

    const candleSeries = chart.addCandlestickSeries({
      upColor: '#00ff88',
      downColor: '#ff4444',
      borderDownColor: '#ff4444',
      borderUpColor: '#00ff88',
      wickDownColor: '#ff4444',
      wickUpColor: '#00ff88',
    })
    candleSeriesRef.current = candleSeries

    const volumeSeries = chart.addHistogramSeries({
      color: '#00d4ff',
      priceFormat: { type: 'volume' },
      priceScaleId: 'left',
    })
    volumeSeries.priceScale().applyOptions({
      scaleMargins: { top: 0.5, bottom: 0 },
    })
    volumeSeriesRef.current = volumeSeries

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight,
        })
      }
    }
    window.addEventListener('resize', handleResize)

    // Fetch data (uses axios so auth interceptors attach the Bearer token)
    axios.get(`${API_BASE}/backtest/instrument-chart`, {
      params: { instrument_id: instrumentId, max_bars: 5000 },
    })
      .then((res) => res.data)
      .then((data) => {
        const bars = data.bars as Array<{
          time: number
          open: number
          high: number
          low: number
          close: number
          volume: number
        }>

        const candleData: CandlestickData[] = bars.map((b) => ({
          time: b.time as any,
          open: b.open,
          high: b.high,
          low: b.low,
          close: b.close,
        }))

        const volumeData: HistogramData[] = bars.map((b) => ({
          time: b.time as any,
          value: b.volume,
          color: b.close >= b.open ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 68, 68, 0.3)',
        }))

        candleSeries.setData(candleData)
        volumeSeries.setData(volumeData)
        chart.timeScale().fitContent()

        setInfo({ total_bars: data.total_bars, displayed_bars: data.displayed_bars })
        setLoading(false)
      })
      .catch((err) => {
        const msg = err.response?.data?.detail || err.message || 'Unknown error'
        setError(msg)
        setLoading(false)
      })

    return () => {
      window.removeEventListener('resize', handleResize)
      chart.remove()
    }
  }, [instrumentId])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-[#111] border border-[#2a2a2a] rounded-xl shadow-2xl w-[90vw] h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a2a2a]">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-white font-mono">{instrumentId}</h2>
            {info && (
              <span className="text-xs text-[#666]">
                {info.displayed_bars.toLocaleString()} bars shown
                {info.total_bars !== info.displayed_bars && (
                  <> (downsampled from {info.total_bars.toLocaleString()})</>
                )}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-[#2a2a2a] text-[#888] hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Chart area */}
        <div className="flex-1 relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="flex items-center gap-3 text-[#888]">
                <Loader2 size={24} className="animate-spin" />
                <span>Loading chart data...</span>
              </div>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="text-red-400 text-sm text-center">
                <p className="font-medium mb-1">Failed to load chart</p>
                <p className="text-[#666]">{error}</p>
              </div>
            </div>
          )}
          <div ref={chartContainerRef} className="w-full h-full" />
        </div>
      </div>
    </div>
  )
}
